/* Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core';
import Chip from '@material-ui/core/Chip';
import {uniqBy} from 'lodash';
import isEmpty from 'lodash.isempty';
import isUndefined from 'lodash.isundefined';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {loadDevices} from '@ion/core/src/services/Connect';
import {Account, Device, DeviceMove} from '@ion/core/src/types/entities';
import {useDevicesDropdown} from '@ion/query/src/hooks/useDevicesDropdown';
import {ButtonSpinner} from '../components/loading/ButtonSpinner';
import Dropdown, {
  DropDownDefaultProps,
  DropDownInfiniteScrollProps,
  Options
} from '../components/menus/Dropdown';
import {RemoveDeviceNotification} from '../notifications/RemoveDeviceNotification';
import {dlsColor, dlsTypography} from '../theme/theme';
import {BodyTypography} from '../typography/BodyTypography';
import {AdminGroupLevelProps} from './AdminGroupEditor';
import {AssetAssociation, AssetEditorShowDefaultProps} from './AssetEditor';
import {getDeviceOptions} from './DeviceChooser';
import GroupChips from './GroupChips';
import {getOptions, getValues} from './ListEditorUtils';
import useDeviceListSelection from './useDeviceListSelection';

// defines the Device list prop definitions ...
export type DeviceListProps = Pick<AssetEditorShowDefaultProps, 'onDeviceAssociated'> &
AssetAssociation & DropDownDefaultProps & AdminGroupLevelProps & DropDownInfiniteScrollProps & {
  accountId: number;
  onChange?: Function;
  readOnly?: boolean;
  required?: boolean;
  classes?: string;
  defaultValue?: any;
  hasDetailedViewLink?: boolean;
  detailedViewLinkText?: string;
  onDetailedLinkClick?: Function;
  loading?: boolean;
  hasError?: boolean;
  setDeviceDetailedRow?: Function;
  isEditMode?: boolean;
  setFormState?: Function;
  viewGroup?: boolean;
  isMultiDevice?: boolean;
  devicePrompt?: boolean;
  account?: Account;
  movedToAccount?: DeviceMove;
  hasDeviceMoveOption?: boolean;
  setRemovedDevice?: Function;
  setMovingToGroup?: Function;
  formState?: object;
  onMoveDevice?: (group: Account) => void;
};

// default styles used by the component when a classes prop is not provided ...
const useStyles = makeStyles((theme) => ({
  root : {
    padding : '0px !important',
    margin  : '0px !important'
  },
  actionLabel : {
    minWidth : '120px'
  },
  boxStyle : {
    marginTop : '14px',
    fontSize  : 'xx-large'
  },
  boxfieldLabel : {
    color        : dlsColor('grey2'),
    marginBottom : '8px',
    ...dlsTypography('body3')
  }
}));

const MIN_INPUT_LENGTH = 0;
const MAX_INPUT_LENGTH = 3;

/**
 * The DeviceList provides a list of devices for the selected account or
 * user's default account for display within a dropdown list and
 * allows the user to select an Asset from the list which will be returned to the
 * client as the selected Asset.
 */
export const DeviceList = ({
  accountId,
  onChange,
  readOnly,
  required,
  classes,
  defaultValue,
  loading,
  hasError,
  isEditMode,
  setFormState,
  setDeviceDetailedRow,
  onDetailedLinkClick,
  viewGroup,
  onDeviceAssociated,
  onChipClick,
  movedToAccount,
  account,
  setRemovedDevice,
  setMovingToGroup,
  formState,
  onMoveDevice,
  isChipClickable = false,
  includeAssetAssociation = false,
  hasDetailedViewLink = false,
  viewChip = false,
  detailedViewLinkText = null,
  isMultiDevice = false,
  hasDeviceMoveOption = false,
  devicePrompt = false,
  isInfiniteScroll = false
}: DeviceListProps) => {
  const _classes                              = useStyles();
  const hasMounted                            = useHasMounted();
  const [isLoading, setIsLoading]             = useState<boolean>(loading);
  const [devices, setDevices]                 = useState<Device[]>([]);
  const [selected, setSelected]               = useState<string[]>([]);
  const [options, setOptions]                 = useState<Options[]>([]);
  const [savedDevices, setSavedDevices]       = useState<Device[]>([]);
  const [pageId, setPageId]                   = useState<number>(1);
  const [isScroll, setIsScroll]               = useState<boolean>(false);
  const [isSearch, setIsSearch]               = useState<boolean>(false);
  const [searchTerms, setSearchTerms]         = useState<string>();
  const [isCleared, setIsCleared]             = useState<boolean>(false);
  const [isOutsideClick, setIsOutsideClick]   = useState<boolean>(false);
  const [scrolledDevices, setScrolledDevices] = useState([]);
  const {loadDevice}                          = useDevicesDropdown();

  const {
    onDeleteSelectedDevice,
    onConfirmRemoveDevice,
    onCancelRemoveDevice,
    isRemoveDevice,
    deletedFormDevices
  } = useDeviceListSelection({
    formState,
    selected,
    setSelected,
    setFormState,
    setRemovedDevice,
    setMovingToGroup,
    onMoveDevice
  });

  // whenever the account changes the Devices are reloaded ...
  useEffect(() => {
    accountId && fetchDevices(accountId);
  }, [accountId]);

  // if a default device item is provided set it as the selected ...
  useEffect(() => {
    if (defaultValue) {
      setSavedDevices(defaultValue);
      setSelected(getValues(defaultValue));
    }
    if (includeAssetAssociation) {
      onDeviceAssociated?.(defaultValue > savedDevices);
    }
  }, [defaultValue]);

  // if a default device item is provided set it as the selected ...
  useEffect(() => {
    if (defaultValue && !isInfiniteScroll) {
      setSelected(getValues(defaultValue));
    }
  }, [options]);

  useEffect(() => {
    if ((isInfiniteScroll && isScroll) ||
    (isInfiniteScroll &&
      (searchTerms?.length > MAX_INPUT_LENGTH || searchTerms?.length === MIN_INPUT_LENGTH)
    )) {
      handleDeviceList();
    }
  }, [pageId, searchTerms, isScroll, isSearch, isCleared, devices, isInfiniteScroll, accountId]);

  useEffect(() => {
    if (isInfiniteScroll && isScroll) {
      setOptions(getOptions(scrolledDevices));
      setIsOutsideClick(false);
    }
  }, [isInfiniteScroll, scrolledDevices]);

  useEffect(() => {
    if (isInfiniteScroll && isOutsideClick) {
      setOptions(getOptions(devices));
    }
  }, [isOutsideClick, devices, isInfiniteScroll]);

  // manages loading assets based on current search term ...
  const fetchDevices = async function(searchTerm: any) {
    let devices: Device[];
    setIsLoading(true);
    try {
      const {devices: items, totalCount} = await loadDevices({
        pageId   : 1,
        pageSize : 100,
        sortOn   : ['+name'],
        accountId
      });
      devices = items;
    } catch (err) {
      setOptions([]);
      error(`Device: Error occurred retrieving Devices for account ${accountId}`);
    } finally {
      if (hasMounted !== false) {
        setDevices(devices);
        if (isMultiDevice) {
          setOptions(getDeviceOptions(devices));
        } else {
          setOptions(getOptions(devices));
        }
        setIsLoading(false);
        if (isUndefined(searchTerms) && isInfiniteScroll) {
          setScrolledDevices(scrolledDevices?.concat(devices));
        }
      }
    }
  };

  const handleDeviceList = useCallback(() => {
    try {
      loadDevice({
        pageSize    : 100,
        pageId      : pageId,
        searchTerms : searchTerms,
        accountId
      }, {
        onSuccess : (device) => {
          setScrolledDevices(scrolledDevices?.concat(device));
          if (isSearch || (searchTerms?.length === MIN_INPUT_LENGTH && isCleared) || !isEmpty(setSelected)) {
            setOptions(getOptions(device));
            setIsOutsideClick(false);
          }
        },
        onError : () => {
          setOptions([]);
        }
      });
    } catch (err) {
      setOptions([]);
    }
  }, [pageId, searchTerms, isScroll, isSearch, isCleared, accountId, isInfiniteScroll]);

  const handleChange = useCallback(function(event: React.ChangeEvent, child = []) {
    const idValues = child.map(({value, id}) => isMultiDevice ? value : id);
    const allDevices = [...devices, ...savedDevices, ...scrolledDevices];
    const deviceList = uniqBy(allDevices?.filter(({id}) => {
      return idValues.includes(id);
    }), 'id');
    setDeviceDetailedRow(child);
    setSelected(child);
    onChange?.(deviceList);
    if (isInfiniteScroll) {
      handleDeviceClickOutside();
    }
  }, [setDeviceDetailedRow, devices, selected, scrolledDevices]);

  const actionLabel = useMemo(() => (
    <span className={_classes.actionLabel}>
      {
        isLoading
          ? <ButtonSpinner />
          : translate('common:editors.shared.continue')
      }
    </span>
  ), [isLoading]);

  const onInfiniteScroll = useCallback((scrollHeight, scrollTop, clientHeight) => {
    if (scrollTop + clientHeight >= scrollHeight - 2) {
      setPageId((prevActiveStep) => prevActiveStep + 1);
      setIsScroll(true);
      setIsSearch(false);
      setIsCleared(false);
    }
  }, []);

  const onSearchDevices = useCallback((value: string) => {
    if (value?.length > MAX_INPUT_LENGTH) {
      setSearchTerms(value);
      setIsSearch(true);
      setPageId(1);
    } else if (value?.length === MIN_INPUT_LENGTH) {
      setSearchTerms('');
      setIsSearch(false);
      setIsCleared(true);
      setScrolledDevices([]);
      setPageId(1);
    }
  }, []);

  const handleDeviceClickOutside = useCallback(() => {
    setIsOutsideClick(true);
    setPageId(1);
    setSearchTerms('');
    setIsSearch(false);
    setIsCleared(true);
    setScrolledDevices([]);
  }, []);

  return (
    <>
      {isMultiDevice && isRemoveDevice && devicePrompt && (
        <RemoveDeviceNotification account={account}
          title={translate('common:editors.delete.remove', {
            selected : deletedFormDevices?.name
          })}
          actionLabel={actionLabel}
          dismissLabel={translate('common:editors.shared.cancel')}
          onConfirm={onConfirmRemoveDevice}
          onCancel={onCancelRemoveDevice}
          movedToAccount={movedToAccount}
          hasDeviceMoveOption={hasDeviceMoveOption}
          show />
      )}
      <div className={classes || _classes.root}>
        {(!viewChip || isEmpty(selected)) && (
          <Dropdown required={required}
            hasError={hasError}
            disabled={readOnly || isLoading || viewGroup}
            onInfiniteScroll={onInfiniteScroll}
            onSearchAssociations={onSearchDevices}
            onHandleClickOutside={handleDeviceClickOutside}
            onChipClick={onChipClick}
            isChipClickable={isChipClickable}
            isInfiniteScroll={isInfiniteScroll}
            isChipDevice
            options={options}
            multiSelect
            loading={isLoading}
            ctaLinkText={detailedViewLinkText}
            hasCTALink={hasDetailedViewLink}
            hasClearAll={!isMultiDevice && isEditMode && !viewGroup}
            onCTALinkClick={onDetailedLinkClick}
            value={isLoading ? translate('common:states.loading') : selected}
            fieldLabel={translate('common:editors.device.label')}
            placeholder={translate('common:editors.device.placeholder')}
            preferPlaceholder={selected.length === 0 && !isLoading}
            onChange={handleChange}
            renderTags={isMultiDevice
              ? function(tagValue, getTagProps) {
                return tagValue?.map?.(({label, value}, index) => (
                  <Chip key={index} label={label}
                    {...getTagProps({index})}
                    disabled={!selected || readOnly}
                    onDelete={onDeleteSelectedDevice(value)}/>
                ));
              }
              : undefined}/>
        )}
      </div>
      <div className={_classes.boxStyle}>
        {!isEmpty(selected) && viewChip && (
          <>
            <BodyTypography className={_classes.boxfieldLabel}>
              {translate('common:editors.device.label')}
            </BodyTypography>
            <GroupChips value={selected}
              onChipClick={onChipClick}
              isDeviceChip
              chips={savedDevices} />
          </>
        )}
      </div>
    </>
  );
};

export default DeviceList;
